import { CLEARMIND_THEME } from "@clearmind/shared";

export function styles(primary: string): string {
  const light = CLEARMIND_THEME.light;
  const dark = CLEARMIND_THEME.dark;

  return `
    @import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

    :host, .cm-root {
      --background: ${light.background};
      --foreground: ${light.foreground};
      --primary: ${primary};
      --primary-foreground: ${light.primaryForeground};
      --muted: ${light.muted};
      --muted-foreground: ${light.mutedForeground};
      --border: ${light.border};
      --accent: ${light.accent};
      --ring: ${primary};
      --radius: ${light.radius};
      --shadow-panel: 0 16px 40px #0f12181a, 0 4px 12px #0f121808;
      --shadow-launcher: 0 8px 24px #0f121824, 0 2px 8px #0f121812;
      --font-sans: "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      font-family: var(--font-sans);
      font-size: 14px;
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
      box-sizing: border-box;
    }

    .cm-root[data-theme="dark"] {
      --background: ${dark.background};
      --foreground: ${dark.foreground};
      --primary-foreground: ${dark.primaryForeground};
      --muted: ${dark.muted};
      --muted-foreground: ${dark.mutedForeground};
      --border: ${dark.border};
      --shadow-panel: 0 24px 56px #0009, 0 8px 20px #00000040;
      --shadow-launcher: 0 8px 28px #00000050;
    }

    @media (prefers-color-scheme: dark) {
      .cm-root[data-theme="auto"] {
        --background: ${dark.background};
        --foreground: ${dark.foreground};
        --primary-foreground: ${dark.primaryForeground};
        --muted: ${dark.muted};
        --muted-foreground: ${dark.mutedForeground};
        --border: ${dark.border};
        --shadow-panel: 0 24px 56px #0009, 0 8px 20px #00000040;
        --shadow-launcher: 0 8px 28px #00000050;
      }
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .cm-root {
      position: fixed;
      bottom: 0;
      z-index: 2147483000;
      pointer-events: none;
    }

    .cm-pos-right { right: 20px; }
    .cm-pos-left { left: 20px; }

    .cm-launcher, .cm-panel { pointer-events: auto; }

    /* ── Launcher ── */
    .cm-launcher {
      position: fixed;
      bottom: 20px;
      width: 56px;
      height: 56px;
      border-radius: 50%;
      border: none;
      background: var(--primary);
      color: var(--primary-foreground);
      cursor: pointer;
      box-shadow: var(--shadow-launcher);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: transform 0.2s cubic-bezier(0.33, 1, 0.68, 1),
                  box-shadow 0.2s ease,
                  opacity 0.2s ease;
    }

    .cm-pos-right .cm-launcher { right: 20px; }
    .cm-pos-left .cm-launcher { left: 20px; }

    .cm-launcher:hover {
      transform: scale(1.06);
      box-shadow: 0 12px 32px #0f121828, 0 4px 12px #0f121816;
    }

    .cm-launcher:active { transform: scale(0.98); }

    .cm-launcher svg { width: 24px; height: 24px; }

    .cm-root.cm-open .cm-launcher {
      opacity: 0;
      pointer-events: none;
      transform: scale(0.85);
    }

    /* ── Panel ── */
    .cm-panel {
      position: fixed;
      bottom: 20px;
      width: min(400px, calc(100vw - 40px));
      height: min(600px, calc(100dvh - 40px));
      background: var(--background);
      color: var(--foreground);
      border: 1px solid var(--border);
      border-radius: calc(var(--radius) + 4px);
      box-shadow: var(--shadow-panel);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      opacity: 0;
      visibility: hidden;
      transform: translateY(16px) scale(0.96);
      transform-origin: bottom right;
      transition: opacity 0.25s cubic-bezier(0.33, 1, 0.68, 1),
                  transform 0.25s cubic-bezier(0.33, 1, 0.68, 1),
                  visibility 0.25s;
    }

    .cm-pos-left .cm-panel { transform-origin: bottom left; }
    .cm-pos-right .cm-panel { right: 20px; }
    .cm-pos-left .cm-panel { left: 20px; }

    .cm-panel.cm-visible {
      opacity: 1;
      visibility: visible;
      transform: translateY(0) scale(1);
    }

    /* ── Header ── */
    .cm-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 16px 16px 14px;
      border-bottom: 1px solid var(--border);
      background: var(--background);
      flex-shrink: 0;
    }

    .cm-header-brand {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
    }

    .cm-logo {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: var(--foreground);
      color: var(--background);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .cm-header-text { min-width: 0; }

    .cm-title {
      display: block;
      font-size: 15px;
      font-weight: 600;
      letter-spacing: -0.02em;
      line-height: 1.3;
      color: var(--foreground);
    }

    .cm-subtitle {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      color: var(--muted-foreground);
      margin-top: 2px;
    }

    .cm-status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: var(--accent);
      flex-shrink: 0;
    }

    .cm-close {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      border: none;
      background: transparent;
      color: var(--muted-foreground);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: background 0.15s ease, color 0.15s ease;
    }

    .cm-close:hover {
      background: var(--muted);
      color: var(--foreground);
    }

    .cm-close svg { width: 18px; height: 18px; }

    /* ── Messages ── */
    .cm-messages {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 16px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      scroll-behavior: smooth;
    }

    .cm-messages::-webkit-scrollbar { width: 6px; }
    .cm-messages::-webkit-scrollbar-thumb {
      background: var(--border);
      border-radius: 99px;
    }

    .cm-msg {
      display: flex;
      width: 100%;
    }

    .cm-msg-user { justify-content: flex-end; }

    .cm-msg-assistant { justify-content: flex-start; }

    .cm-msg-inner {
      display: flex;
      gap: 10px;
      max-width: 92%;
      align-items: flex-end;
    }

    .cm-msg-user .cm-msg-inner {
      flex-direction: row-reverse;
      max-width: 85%;
    }

    .cm-avatar {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      background: var(--muted);
      color: var(--foreground);
      font-size: 10px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      letter-spacing: -0.02em;
    }

    .cm-msg-user .cm-avatar { display: none; }

    .cm-msg-body {
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
    }

    .cm-bubble {
      padding: 10px 14px;
      border-radius: var(--radius);
      word-break: break-word;
      font-size: 14px;
      line-height: 1.65;
    }

    .cm-msg-user .cm-bubble {
      background: var(--primary);
      color: var(--primary-foreground);
      border-bottom-right-radius: 4px;
    }

    .cm-msg-assistant .cm-bubble {
      background: var(--muted);
      color: var(--foreground);
      border-bottom-left-radius: 4px;
    }

    .cm-bubble a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
      opacity: 0.95;
    }

    .cm-msg-user .cm-bubble a {
      color: var(--primary-foreground);
      font-weight: 500;
    }

    .cm-msg-assistant .cm-bubble a {
      color: var(--primary);
      font-weight: 500;
    }

    .cm-bubble strong { font-weight: 600; }

    .cm-bubble p {
      margin: 0;
    }

    .cm-bubble p + p { margin-top: 0.75em; }

    .cm-bubble ul, .cm-bubble ol {
      margin: 0.45em 0 0.2em 1.25em;
      padding: 0;
    }

    .cm-bubble li {
      padding-left: 0.15em;
    }

    .cm-bubble li + li {
      margin-top: 0.3em;
    }

    /* ── Typing indicator ── */
    .cm-typing {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 0;
    }

    .cm-typing span {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: var(--muted-foreground);
      opacity: 0.5;
      animation: cm-bounce 1.2s ease-in-out infinite;
    }

    .cm-typing span:nth-child(2) { animation-delay: 0.15s; }
    .cm-typing span:nth-child(3) { animation-delay: 0.3s; }

    @keyframes cm-bounce {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
      30% { transform: translateY(-4px); opacity: 1; }
    }

    /* ── Source chips ── */
    .cm-sources {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .cm-sources-label {
      width: 100%;
      font-size: 11px;
      font-weight: 500;
      color: var(--muted-foreground);
      letter-spacing: 0.02em;
      text-transform: uppercase;
      margin-bottom: 2px;
    }

    .cm-source {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      max-width: 100%;
      font-size: 12px;
      font-weight: 500;
      line-height: 1.3;
      padding: 5px 10px;
      border-radius: 999px;
      background: var(--background);
      border: 1px solid var(--border);
      color: var(--foreground);
      text-decoration: none;
      transition: border-color 0.15s ease, background 0.15s ease;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .cm-source:hover {
      border-color: color-mix(in srgb, var(--primary) 40%, var(--border));
      background: var(--muted);
    }

    .cm-source svg {
      width: 12px;
      height: 12px;
      flex-shrink: 0;
      color: var(--muted-foreground);
    }

    /* ── Feedback ── */
    .cm-feedback {
      display: flex;
      gap: 6px;
      margin-top: 2px;
    }

    .cm-feedback-btn {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      border: 1px solid var(--border);
      background: var(--background);
      color: var(--muted-foreground);
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
    }

    .cm-feedback-btn svg { width: 14px; height: 14px; }

    .cm-feedback-btn:hover:not(:disabled) {
      border-color: color-mix(in srgb, var(--primary) 35%, var(--border));
      color: var(--foreground);
    }

    .cm-feedback-btn.cm-active {
      border-color: var(--primary);
      color: var(--primary);
      background: color-mix(in srgb, var(--primary) 10%, var(--background));
    }

    .cm-feedback-btn:disabled { cursor: default; opacity: 0.85; }

    /* ── Quick actions ── */
    .cm-quick-actions {
      display: flex;
      gap: 8px;
      padding: 0 16px 12px;
      overflow-x: auto;
      flex-shrink: 0;
      scrollbar-width: none;
      -ms-overflow-style: none;
    }

    .cm-quick-actions::-webkit-scrollbar { display: none; }

    .cm-chip {
      flex-shrink: 0;
      font-size: 12px;
      font-weight: 500;
      padding: 7px 12px;
      border-radius: 999px;
      border: 1px solid var(--border);
      background: var(--background);
      color: var(--foreground);
      cursor: pointer;
      white-space: nowrap;
      transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
    }

    .cm-chip:hover {
      border-color: color-mix(in srgb, var(--primary) 35%, var(--border));
      background: var(--muted);
    }

    .cm-chip:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    /* ── Human handoff ── */
    .cm-lead-form, .cm-lead-saved {
      margin: 0 16px 12px;
      padding: 12px;
      border: 1px solid color-mix(in srgb, var(--primary) 28%, var(--border));
      border-radius: var(--radius);
      background: color-mix(in srgb, var(--primary) 6%, var(--background));
      display: grid;
      gap: 8px;
      flex-shrink: 0;
    }

    .cm-lead-form strong { font-size: 13px; }
    .cm-lead-form p, .cm-lead-form label, .cm-lead-saved {
      color: var(--muted-foreground);
      font-size: 12px;
      line-height: 1.45;
    }

    .cm-lead-form input:not([type="checkbox"]) {
      width: 100%;
      padding: 8px 10px;
      border: 1px solid var(--border);
      border-radius: 8px;
      background: var(--background);
      color: var(--foreground);
      font: inherit;
      font-size: 13px;
    }

    .cm-lead-form label {
      display: flex;
      align-items: flex-start;
      gap: 7px;
    }

    .cm-lead-form input[type="checkbox"] { margin-top: 3px; accent-color: var(--primary); }
    .cm-lead-form button {
      border: none;
      border-radius: 8px;
      padding: 9px 12px;
      background: var(--primary);
      color: var(--primary-foreground);
      cursor: pointer;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
    }

    .cm-lead-form button:disabled { cursor: wait; opacity: 0.6; }
    .cm-lead-error { color: #b42318 !important; }

    /* ── Footer / input ── */
    .cm-footer {
      border-top: 1px solid var(--border);
      padding: 12px 16px 14px;
      background: var(--background);
      flex-shrink: 0;
    }

    .cm-form {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 10px;
    }

    .cm-input-wrap {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    }

    .cm-input {
      width: 100%;
      padding: 10px 14px;
      border: 1px solid var(--border);
      border-radius: var(--radius);
      background: var(--background);
      color: var(--foreground);
      font-family: inherit;
      font-size: 14px;
      line-height: 1.4;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    .cm-input::placeholder { color: var(--muted-foreground); }

    .cm-input:focus {
      outline: none;
      border-color: color-mix(in srgb, var(--primary) 50%, var(--border));
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 15%, transparent);
    }

    .cm-send {
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: none;
      border-radius: var(--radius);
      background: var(--primary);
      color: var(--primary-foreground);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.15s ease, transform 0.15s ease;
    }

    .cm-send:hover:not(:disabled) { opacity: 0.92; }
    .cm-send:active:not(:disabled) { transform: scale(0.96); }

    .cm-send:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    .cm-send svg { width: 18px; height: 18px; }

    .cm-disclaimer {
      font-size: 11px;
      color: var(--muted-foreground);
      line-height: 1.45;
      text-align: center;
    }

    /* ── Mobile ── */
    @media (max-width: 480px) {
      .cm-pos-right, .cm-pos-left { right: 0; left: 0; }

      .cm-panel {
        width: 100vw;
        height: 100dvh;
        bottom: 0;
        right: 0 !important;
        left: 0 !important;
        border-radius: 0;
        border-left: none;
        border-right: none;
        border-bottom: none;
      }

      .cm-panel.cm-visible {
        transform: translateY(0) scale(1);
      }

      .cm-launcher {
        bottom: 16px;
        right: 16px !important;
        left: auto !important;
      }

      .cm-root.cm-open .cm-launcher { display: none; }
    }
  `;
}
